import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import palette from 'core-system/Themes/palette'
import Tooltip from '@mui/material/Tooltip'
import ClickAwayListener from '@mui/material/ClickAwayListener'

const SPACING = 8

const scrollOffset = (anchor: HTMLElement, parent: HTMLElement | null) => {
  let top = 0
  let left = 0
  if (!parent) {
    return { top, left }
  }
  let node: HTMLElement | null = anchor.parentElement
  while (node) {
    top += node.scrollTop
    left += node.scrollLeft
    if (node === parent) {
      break
    }
    node = node.parentElement
  }
  return { top, left }
}

const SubMenuContainer = styled.div<{
  top: number
  left: number
  position: 'top' | 'bottom'
}>`
  position: absolute;
  z-index: ${(p) => p.theme.zIndex.tooltip};
  top: ${(p) => p.top}px;
  min-width: 10rem;
  left: ${(p) => p.left}px;
  transform: ${(p) =>
    p.position === 'bottom' ? 'translateY(0)' : 'translateY(-100%)'};
`

const SubMenuItems = styled.div`
  ${(p) => p.theme.typography.action4};
  display: flex;
  flex-direction: column;
  border: 0.0625rem solid ${(p) => p.theme.palette.grey.grey300};
  box-shadow: ${(p) => p.theme.dropShadows.selected};
  background-color: ${(p) => p.theme.palette.white};
  border-radius: 0.25rem;
  padding: 0.5rem 0;
  cursor: pointer;
`

const SubMenuItem = styled.div<{ canHover?: boolean; active?: boolean }>`
  text-decoration: none;
  padding: 0.75rem 1rem;
  color: ${({ active }) =>
    active ? palette.secondary.purple600 : palette.text.primary};
  font-weight: 500;
  line-height: 0.875rem;

  &:hover {
    background-color: ${({ canHover }) =>
      !canHover ? 'transparent' : palette.secondary.purple200};
  }
`

interface DropdownMenuProps {
  anchorRef: React.RefObject<HTMLElement>
  menuItems: {
    label?: string
    element?: React.ReactNode
    onClick?: () => void
    canHover?: boolean
    active?: boolean
    tooltip?: string
  }[]
  position?: 'top' | 'bottom'
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  anchorRef,
  menuItems,
  position = 'bottom',
}) => {
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  // Toggle on anchor click, recalc position when opening
  useEffect(() => {
    const anchor = anchorRef.current
    if (!anchor) return

    const onClick = () => {
      setVisible((prev) => {
        if (!prev) {
          const anchorRect = anchor.getBoundingClientRect()
          const parent = anchor.offsetParent as HTMLElement | null
          const parentRect = parent?.getBoundingClientRect() ?? {
            top: 0,
            left: 0,
          }
          const scrolled = scrollOffset(anchor, parent)

          // Viewport deltas ignore scroll. Absolute coordinates need it back
          // so the menu stays on the button inside a scrolled drawer.
          const left = anchorRect.left - parentRect.left + scrolled.left
          const baseTop =
            position === 'bottom' ? anchorRect.bottom : anchorRect.top
          const top =
            baseTop -
            parentRect.top +
            scrolled.top +
            (position === 'bottom' ? SPACING : -SPACING)

          setCoords({ top, left })
        }
        return !prev
      })
    }

    anchor.addEventListener('click', onClick)
    return () => anchor.removeEventListener('click', onClick)
  }, [anchorRef, position])

  // Auto-close on scroll
  useEffect(() => {
    if (!visible) return
    const handleScroll = () => setVisible(false)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [visible])

  if (!visible) return null

  return (
    <ClickAwayListener onClickAway={() => setVisible(false)}>
      <SubMenuContainer
        ref={containerRef}
        top={coords.top}
        left={coords.left}
        position={position}
      >
        <SubMenuItems>
          {menuItems.map((item, i) => (
            <Tooltip key={i} title={item.tooltip ?? ''}>
              <SubMenuItem
                onClick={item.onClick}
                canHover={item.canHover ?? true}
                active={item.active}
              >
                {item.label ?? item.element}
              </SubMenuItem>
            </Tooltip>
          ))}
        </SubMenuItems>
      </SubMenuContainer>
    </ClickAwayListener>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  DropdownMenu.displayName = 'DropdownMenu'
}

export default React.memo(DropdownMenu)
